export const SHOPIFY_REAUTH_RETURN_PATH_KEY =
  'shipaid___shopify-reauthorization-return-path';

type ReauthorizationStorage = Pick<
  Storage,
  'getItem' | 'removeItem' | 'setItem'
>;

type ReauthorizationDependencies = {
  storage?: ReauthorizationStorage;
  redirect?: (url: string) => void;
  appOrigin?: string;
};

const isSafeRelativePath = (path: string) =>
  path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') &&
  !Array.from(path).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);

export const isShopifyHost = (host: string) =>
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.myshopify\.com$/.test(host);

export const getShopifyAuthorizationLink = (
  value: string,
  appOrigin = window.location.origin,
): string => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.hash || url.href !== value) throw new Error();
    const shopifyAuthorization = !url.port && isShopifyHost(url.hostname) && url.pathname === '/admin/oauth/authorize';
    const ownedLaunch = url.origin === new URL(appOrigin).origin && url.pathname === '/post-install' &&
      url.searchParams.getAll('install').length === 1 && url.searchParams.get('install') === '1' &&
      url.searchParams.getAll('shop').length === 1 && isShopifyHost(url.searchParams.get('shop') || '') &&
      Array.from(url.searchParams.keys()).every(key => key === 'install' || key === 'shop');
    if (!shopifyAuthorization && !ownedLaunch) throw new Error();
    return url.href;
  } catch {
    throw new Error('ShipAid did not return a valid Shopify authorization link.');
  }
};

export const getCurrentRelativePath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

export const beginShopifyReauthorization = (
  installLink: string,
  returnPath = getCurrentRelativePath(),
  dependencies: ReauthorizationDependencies = {},
) => {
  if (!installLink) {
    throw new Error('Shopify did not return an authorization link.');
  }

  const authorizationLink = getShopifyAuthorizationLink(installLink, dependencies.appOrigin);

  const storage = dependencies.storage || window.sessionStorage;
  const redirect =
    dependencies.redirect || ((url: string) => window.location.assign(url));
  const safeReturnPath = isSafeRelativePath(returnPath) ? returnPath : '/';

  storage.setItem(SHOPIFY_REAUTH_RETURN_PATH_KEY, safeReturnPath);
  redirect(authorizationLink);
};

export const consumeShopifyReauthorizationReturnPath = (
  fallbackPath: string,
  storage: ReauthorizationStorage = window.sessionStorage,
) => {
  const returnPath = storage.getItem(SHOPIFY_REAUTH_RETURN_PATH_KEY);
  storage.removeItem(SHOPIFY_REAUTH_RETURN_PATH_KEY);

  return returnPath && isSafeRelativePath(returnPath)
    ? returnPath
    : fallbackPath;
};
